import {useCallback} from 'react';

import {
  type Account,
  AccountPlanType,
  AccountType,
} from '../../components/Search/SearchNav/types';
import {
  UsageType,
  useBillingPeriodSubscriptionsQuery,
} from '../../generated/graphql';
import {
  getWbAgentSub,
  getWbagentSubBillingPeriod,
  useAccountPrivileges,
} from '../accounts/pricing';
import {useRampFlagEnableWbAgentUsageCharts} from '../rampFeatureFlags/rampFeatureFlags';
import {
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  getIntervalMarkersFromSubscription,
  useGetUsageThisBillingPeriod,
} from './utils';

export function isWbAgentBillingPlanEligible(account: Account): boolean {
  return (
    account.accountPlanType === AccountPlanType.Pro ||
    account.accountPlanType === AccountPlanType.Enterprise
  );
}

/** Call only when `orgName` is known (e.g. from the URL or account selector). */
export function useIsWbAgentBillingUsageVisible(orgName: string): {
  visible: boolean;
  loading: boolean;
} {
  const {enabled, loading} = useRampFlagEnableWbAgentUsageCharts(orgName);
  return {
    visible: !loading && enabled,
    loading,
  };
}

function useWbAgentBillingUsageVisibility(orgName: string | null): {
  visible: boolean;
  loading: boolean;
} {
  const hasOrg = orgName != null;

  // When there is no org, pass null so useOrgNameRampFlag skips the query
  // (its skip condition is `orgName == null`). Treat the flag as false.
  const {enabled, loading: rampLoading} = useRampFlagEnableWbAgentUsageCharts(
    (hasOrg ? orgName : null) as string
  );

  if (!hasOrg) {
    return {visible: false, loading: false};
  }

  return {
    visible: !rampLoading && enabled,
    loading: rampLoading,
  };
}

/**
 * Hook to get WB Agent usage details for an account.
 *
 * NOTE: This hook is for multi-tenant (SaaS) deployments only. Do NOT use in
 * single-tenant (server) deployments. For server deployments, use the hooks in
 * ServerCurrentBillingPeriodUsageSection.tsx.
 */
export function useGetWbagentUsageDetails(selectedAccount: Account | null) {
  const orgName =
    selectedAccount?.accountType === AccountType.Personal
      ? null
      : (selectedAccount?.name ?? null);
  const {visible: isRampVisible, loading: visibilityLoading} =
    useWbAgentBillingUsageVisibility(orgName);
  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {privileges, refetch: privilegesRefetch} = privilegeResults;
  const {wbagentLimits} = privileges;

  const isEligible =
    selectedAccount != null && isWbAgentBillingPlanEligible(selectedAccount);

  // billingPeriodSkip
  const billingPeriodQuerySkip =
    selectedAccount?.name == null || visibilityLoading || !isRampVisible;

  const billingPeriodResults = useBillingPeriodSubscriptionsQuery({
    variables: {orgName: billingPeriodQuerySkip ? '' : selectedAccount.name},
    skip: billingPeriodQuerySkip,
  });
  const organization = billingPeriodResults.data?.organization ?? undefined;
  const stripeSubscription = getWbAgentSub(organization);

  // TODO: Right now, we only check for stripe subscriptions. Once Orb integration is added, we will need to check for enterprise subscriptions.
  // At this point, we will also want to update some of the tests in wbagent.test.tsx
  const isUsagePaid = stripeSubscription != null;

  const shouldTrackUsage =
    isEligible || wbagentLimits.wbagentFreeLimit > 0 || isUsagePaid;

  // usageQuerySkip
  const usageQuerySkip = billingPeriodQuerySkip || !shouldTrackUsage;

  // TODO: Once Orb integration is added, prefer enterprise subscription billing
  // period over privileges (see inference.ts finalLimitDateInterval pattern).
  const usagePeriod = selectedAccount?.isEnterprise
    ? getIntervalMarkersFromLimitDateInterval(
        privileges.wbagentLimitDateInterval
      )
    : (getIntervalMarkersFromSubscription(
        getWbagentSubBillingPeriod(
          selectedAccount?.accountPlanType,
          organization
        )
      ) ?? getIntervalMarkersFromCurrentCalendarMonth());

  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.WbAgentCost,
    usagePeriod,
    usageQuerySkip
  );

  const loading =
    visibilityLoading ||
    billingPeriodResults.loading ||
    privilegeResults.loading ||
    usageResults.loading;
  const error =
    billingPeriodResults.error ?? privilegeResults.error ?? usageResults.error;
  const [billingPeriodStart, billingPeriodEnd] = usagePeriod;
  const {usage, refetch: usageRefetch} = usageResults;
  const {refetch: billingPeriodRefetch} = billingPeriodResults;

  const refetch = useCallback(async (): Promise<void> => {
    // Skip-guarded because Apollo refetch fires even when the query is skipped.
    if (usageQuerySkip) {
      return;
    }

    // Only usage changes often, so refetch just that, plus any other query
    // still in error.
    await Promise.allSettled([
      usageRefetch(),
      billingPeriodResults.error != null ? billingPeriodRefetch() : null,
      privilegeResults.error != null ? privilegesRefetch() : null,
    ]);
  }, [
    usageQuerySkip,
    usageRefetch,
    billingPeriodRefetch,
    privilegesRefetch,
    billingPeriodResults.error,
    privilegeResults.error,
  ]);

  return {
    isUsagePaid,
    // TODO(dha-beep): This approach should be refactored (described below)
    // `isBillingStatusLoading` exists because `isUsagePaid` returns a
    // "confidently wrong" `false` while the billing query is still loading,
    // which briefly misclassifies a paying org as a trial (the trial-UI flash).
    // Exposing this separate flag lets consumers know when `isUsagePaid` is not
    // yet trustworthy.
    //
    // This is a deliberate short-term fix, and it's a footgun: the paid status
    // and its loading state are two independent fields, so anyone adding a
    // query-backed `isUsagePaid` (e.g. the sandboxes/training TODOs) must
    // remember to also update this flag and wire it into the consuming UI, or
    // the flash silently regresses.
    //
    // The plan right now is to wait for a refactor that involves both the backend and the frontend: have Go send
    // canonical, already-derived SKU values (billing period, usage, paid/trial
    // status, etc.) to the frontend via GraphQL, instead of the frontend
    // reconstructing them from raw subscription data and keeping that logic in
    // sync with Go. Once the backend owns that derivation, this loading-state
    // workaround (and the whole notion of the frontend guessing paid status)
    // goes away — so we're intentionally not investing in a frontend
    // abstraction here.
    isBillingStatusLoading: visibilityLoading || billingPeriodResults.loading,
    wbagentLimits,
    billingPeriodStart,
    billingPeriodEnd,
    usage,
    refetch,
    loading,
    error,
    isVisible: isRampVisible && isEligible,
    visibilityLoading,
    isFeatureEnabled: isRampVisible,
  };
}
